import { getLanguagePolicyById } from "@/features/playground/config/backend-language-contract.generated";
import type { AllowedRuntime } from "../components/workspace/use-solution-draft";

/**
 * Which runtime a new Judge draft opens on, `EX-PUB21` (finding 14 of the
 * "Judge pages" proposal): the one the reader used last, else C++ at the
 * version the runner itself defaults to. Before, a new draft took the graded
 * catalogue's first entry, which is C99, with its Hello World.
 *
 * Kept per browser, like the drafts themselves, and shared by the practice
 * page and the contest workspace: a reader who writes C++ in one expects C++
 * in the other. The version comes from the language contract, never a literal.
 */
const LAST_RUNTIME_KEY = "judge:last-runtime";
const FALLBACK_LANGUAGE = "cpp";

export function readLastRuntime(): AllowedRuntime | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LAST_RUNTIME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AllowedRuntime>;
    if (typeof parsed.languageKey === "string" && typeof parsed.languageVersion === "string") {
      return { languageKey: parsed.languageKey, languageVersion: parsed.languageVersion };
    }
  } catch {
    // A corrupt entry is just no entry.
  }
  return null;
}

export function rememberRuntime(runtime: AllowedRuntime): void {
  try {
    window.localStorage.setItem(
      LAST_RUNTIME_KEY,
      JSON.stringify({ languageKey: runtime.languageKey, languageVersion: runtime.languageVersion }),
    );
  } catch {
    // Quota or private mode: the next draft opens on the default instead.
  }
}

export function preferredJudgeRuntime(): AllowedRuntime | null {
  const last = readLastRuntime();
  if (last) return last;
  const version = getLanguagePolicyById(FALLBACK_LANGUAGE)?.defaultVersionId.runner;
  return version ? { languageKey: FALLBACK_LANGUAGE, languageVersion: version } : null;
}
